import { useState, useRef, useCallback } from 'react';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

export interface CompressionProgress {
  stage: 'loading' | 'compressing' | 'done';
  progress: number;
  message: string;
}

export function useVideoCompression() {
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState<CompressionProgress>({
    stage: 'loading',
    progress: 0,
    message: ''
  });
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const loadedRef = useRef(false);

  const loadFFmpeg = useCallback(async () => {
    if (loadedRef.current && ffmpegRef.current) return ffmpegRef.current;
    
    const ffmpeg = new FFmpeg();
    ffmpegRef.current = ffmpeg;

    ffmpeg.on('progress', ({ progress }) => {
      setCompressionProgress({
        stage: 'compressing',
        progress: Math.round(progress * 100),
        message: `Compressing: ${Math.round(progress * 100)}%`
      });
    });

    setCompressionProgress({
      stage: 'loading',
      progress: 0,
      message: 'Loading video processor...'
    });

    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });

    loadedRef.current = true;
    return ffmpeg;
  }, []);

  const compressVideo = useCallback(async (file: File): Promise<File> => {
    // Skip compression for small files (under 20MB)
    if (file.size < 20 * 1024 * 1024) {
      return file;
    }

    setIsCompressing(true);
    
    try {
      const ffmpeg = await loadFFmpeg();
      
      const inputName = 'input' + getExtension(file.name);
      const outputName = 'output.mp4';

      setCompressionProgress({
        stage: 'compressing',
        progress: 0,
        message: 'Preparing video...'
      });

      await ffmpeg.writeFile(inputName, await fetchFile(file));

      // Compress with good quality/size balance
      // -crf 28: Good quality with smaller size (lower = better quality, larger file)
      // -preset fast: Balance between speed and compression
      // -vf scale: Limit resolution to 720p max for web viewing
      await ffmpeg.exec([
        '-i', inputName,
        '-c:v', 'libx264',
        '-crf', '28',
        '-preset', 'fast',
        '-vf', 'scale=min(iw\\,1280):min(ih\\,720):force_original_aspect_ratio=decrease',
        '-c:a', 'aac',
        '-b:a', '128k',
        '-movflags', '+faststart',
        outputName
      ]);

      const data = await ffmpeg.readFile(outputName) as Uint8Array;
      // Create a new ArrayBuffer from the data to ensure compatibility
      const buffer = new ArrayBuffer(data.length);
      const view = new Uint8Array(buffer);
      view.set(data);
      const compressedBlob = new Blob([buffer], { type: 'video/mp4' });
      
      // Clean up
      await ffmpeg.deleteFile(inputName);
      await ffmpeg.deleteFile(outputName);

      setCompressionProgress({
        stage: 'done',
        progress: 100,
        message: 'Compression complete!'
      });

      const originalSizeMB = (file.size / 1024 / 1024).toFixed(1);
      const compressedSizeMB = (compressedBlob.size / 1024 / 1024).toFixed(1);
      console.log(`Video compressed: ${originalSizeMB}MB → ${compressedSizeMB}MB`);

      // Create new file with original name but .mp4 extension
      const newFileName = file.name.replace(/\.[^/.]+$/, '') + '_compressed.mp4';
      return new File([compressedBlob], newFileName, { type: 'video/mp4' });
    } finally {
      setIsCompressing(false);
    }
  }, [loadFFmpeg]);

  return {
    compressVideo,
    isCompressing,
    compressionProgress
  };
}

function getExtension(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext ? `.${ext}` : '.mp4';
}
